/**
 * Supabase Client Singleton
 * 
 * IMPORTANT: This is the ONLY place where we create a Supabase client instance.
 * All other files should import from here to avoid "Multiple GoTrueClient instances" warning.
 */

import { createClient as createSupabaseClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { supabaseRequestInterceptor } from './api/interceptors';

// Construct Supabase URL from project ID
const supabaseUrl = `https://${projectId}.supabase.co`;
const supabaseAnonKey = publicAnonKey;

// Singleton instance
let supabaseInstance: SupabaseClient<Database> | null = null;

/**
 * Get or create the Supabase client singleton
 * 
 * Configured with:
 * - Request interceptors (logging, timeouts, cold start handling)
 * - Auth persistence
 * - Auto token refresh
 */
function getSupabaseInstance(): SupabaseClient<Database> {
  if (!supabaseInstance) {
    console.log('[SUPABASE] Creating singleton client instance');
    console.log('[SUPABASE] URL:', supabaseUrl);
    
    supabaseInstance = createSupabaseClient<Database>(
      supabaseUrl,
      supabaseAnonKey,
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
        global: {
          // Intercept all requests for logging, timeouts, and retry logic
          fetch: supabaseRequestInterceptor(fetch.bind(globalThis)),
        },
      }
    );
  }
  
  return supabaseInstance;
}

// Export the singleton instance
export const supabase = getSupabaseInstance();

// Export createClient helper for custom instances (returns the same singleton)
export const createClient = () => supabase;

// Helper to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper to sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};
